import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Overview from "@/pages/Overview";
import Leaderboard from "@/pages/Leaderboard";
import BotDetail from "@/pages/BotDetail";
import Symbols from "@/pages/Symbols";
import Heatmap from "@/pages/Heatmap";
import AIDecisions from "@/pages/AIDecisions";
import Candidates from "@/pages/Candidates";
import HallOfFame from "@/pages/HallOfFame";
import LiveTrading from "@/pages/LiveTrading";
import SymbolLeaders from "@/pages/SymbolLeaders";
import StrategyLeaders from "@/pages/StrategyLeaders";
import Upload from "@/pages/Upload";
import Bridge from "@/pages/Bridge";
import SeedSymbols from "@/pages/SeedSymbols";
import BacktestLog from "@/pages/BacktestLog";
import Portfolios from "@/pages/Portfolios";
import BrainInsights from "@/pages/BrainInsights";
import MorningReport from "@/pages/MorningReport";
import Mt5AdminOverview from "@/pages/mt5admin/Mt5AdminOverview";
import Mt5Portfolio from "@/pages/mt5admin/Portfolio";
import Mt5PassedBots from "@/pages/mt5admin/PassedBots";
// Phase 2: neutral hub + MT5 / cTrader landing pages replace the SB-centric
// storefront at the root. Legacy SB pages (Store, PortfolioDetail, Subscribe,
// BehindTheBuild, Help) intentionally unrouted — retained on disk for
// internal reference but no public path.
import Hub from "@/pages/store/Hub";
import Mt5Landing from "@/pages/store/Mt5Landing";
import Setup from "@/pages/store/Setup";
import Pricing from "@/pages/store/Pricing";
import TrackRecord from "@/pages/store/TrackRecord";
import NotifyPage from "@/pages/store/NotifyPage";
import CookieConsent from "@/components/CookieConsent";
import LaunchPopup from "@/components/LaunchPopup";
import ScrollToHash from "@/components/ScrollToHash";
import HelpWidget from "@/pages/store/HelpWidget";
import StoreCustomers from "@/pages/mt5admin/StoreCustomers";
import StoreEmails from "@/pages/mt5admin/StoreEmails";
import CheckoutSuccess from "@/pages/store/CheckoutSuccess";
import CheckoutCancel from "@/pages/store/CheckoutCancel";
import Login from "@/pages/Login";
import RiskBanner from "@/pages/store/RiskBanner";
import Legal from "@/pages/store/Legal";
import BotPage from "@/pages/store/mt5/BotPage";
import AccountAuth from "@/pages/store/account/AccountAuth";
import AccountDashboard from "@/pages/store/account/AccountDashboard";
import SetupGuide from "@/pages/store/account/SetupGuide";
import { AuthProvider } from "@/auth/AuthProvider";
import PrivateRoute from "@/auth/PrivateRoute";

/** Storefront-only shell (no sidebar). */
function StorefrontShell() {
  return (
    <>
    <RiskBanner />
    <CookieConsent />
    <LaunchPopup />
    <ScrollToHash />
    <HelpWidget />
    <Routes>
      <Route path="/" element={<Hub />} />
      <Route path="/mt5" element={<Mt5Landing />} />
      <Route path="/mt5/bot/:id" element={<BotPage />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/account" element={<AccountDashboard />} />
      <Route path="/account/setup" element={<SetupGuide />} />
      <Route path="/account/login" element={<AccountAuth mode="login" />} />
      <Route path="/account/register" element={<AccountAuth mode="register" />} />
      <Route path="/account/forgot" element={<AccountAuth mode="forgot" />} />
      <Route path="/account/reset" element={<AccountAuth mode="reset" />} />
      {/* cTrader Copy hidden for software-only launch; CtraderLanding kept for future use */}
      <Route path="/ctrader" element={<Navigate to="/" replace />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/track-record" element={<TrackRecord />} />
      <Route path="/notify" element={<NotifyPage />} />
      {/* Legacy /store paths redirect to hub during Phase 2 transition. */}
      <Route path="/store" element={<Navigate to="/" replace />} />
      <Route path="/store/behind-the-build" element={<Navigate to="/" replace />} />
      <Route path="/store/help" element={<Navigate to="/" replace />} />
      <Route path="/store/portfolio/:id" element={<Navigate to="/" replace />} />
      <Route path="/store/subscribe/:tierId" element={<Navigate to="/" replace />} />
      <Route path="/store/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/store/checkout/cancel" element={<CheckoutCancel />} />
    </Routes>
    </>
  );
}

/** Fires a page view on every route change - covers admin lab + storefront. */
function RouteViewTracker() {
  const loc = useLocation();
  React.useEffect(() => {
    try {
      import("./lib/storeApi").then(({ storeApi }) => {
        storeApi.post("/store/track", {
          path: loc.pathname,
          referrer: document.referrer || "",
        }).catch(() => {});
      }).catch(() => {});
    } catch (_e) { /* never break the UI on tracking */ }
  }, [loc.pathname]);
  return null;
}

/** Dashboard shell (sidebar + private pages). */
function DashboardShell() {
  return (
    <div className="md:flex min-h-screen relative">
      <Sidebar />
      <RouteViewTracker />
      <main className="flex-1 min-w-0 relative overflow-x-hidden">
        <Routes>
          <Route path="/admin" element={<Overview />} />
          <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/bot/:id" element={<BotDetail />} />
          <Route path="/symbols" element={<Symbols />} />
          <Route path="/heatmap" element={<Heatmap />} />
          <Route path="/decisions" element={<AIDecisions />} />
          <Route path="/candidates" element={<Candidates />} />
          <Route path="/hall-of-fame" element={<HallOfFame />} />
          <Route path="/symbol-leaders" element={<SymbolLeaders />} />
          <Route path="/strategy-leaders" element={<StrategyLeaders />} />
          <Route path="/live" element={<LiveTrading />} />
          <Route path="/bridge" element={<Bridge />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/backtest-log" element={<BacktestLog />} />
          <Route path="/admin/portfolios" element={<Portfolios />} />
          <Route path="/admin/brain" element={<BrainInsights />} />
          <Route path="/admin/morning-report" element={<MorningReport />} />
          <Route path="/portfolios" element={<Navigate to="/admin/portfolios" replace />} />
          <Route path="/config/seed-symbols" element={<SeedSymbols />} />
        </Routes>
      </main>
    </div>
  );
}

/** Phase 2 - MT5 lab shell. JWT-gated but no SB sidebar so the two
 * admin surfaces stay visually and structurally isolated. */
function Mt5LabShell() {
  return (
    <Routes>
      <Route path="/admin-mt5" element={<Mt5AdminOverview />} />
      <Route path="/admin-mt5/portfolio" element={<Mt5Portfolio />} />
      <Route path="/admin-mt5/passed" element={<Mt5PassedBots />} />
      <Route path="/admin-mt5/customers" element={<StoreCustomers />} />
      <Route path="/admin-mt5/emails" element={<StoreEmails />} />
    </Routes>
  );
}

/** Router shell: decides storefront vs SB admin vs MT5 admin. */
function Shell() {
  const { pathname } = useLocation();
  const isStorefront =
    pathname === "/" ||
    pathname === "/mt5" ||
    pathname.startsWith("/mt5/") ||
    pathname === "/legal" ||
    pathname === "/account" ||
    pathname.startsWith("/account/") ||
    pathname === "/ctrader" ||
    pathname === "/setup" ||
    pathname === "/pricing" ||
    pathname === "/track-record" ||
    pathname === "/notify" ||
    pathname.startsWith("/store") ||
    pathname === "/help";
  const isMt5Admin = pathname.startsWith("/admin-mt5");

  if (pathname === "/login") return <Login />;
  if (isStorefront) return <StorefrontShell />;
  if (isMt5Admin) {
    return (
      <PrivateRoute>
        <Mt5LabShell />
      </PrivateRoute>
    );
  }
  return (
    <PrivateRoute>
      <DashboardShell />
    </PrivateRoute>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App grain">
          <Shell />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
